/**
 * Formatting helpers shared between the WeeklyCalendar and AppointmentForm.
 *
 * - ISO ↔ "YYYY-MM-DD" / "HH:mm" converters for the native date/time inputs.
 * - Duration math in minutes.
 * - Staff conflict detection used by the form step-3 warnings.
 */
import type { AppointmentRecord } from './types';

export function toDateInputValue(iso: string | Date): string {
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  const y = d.getFullYear();
  const m = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function toTimeInputValue(iso: string | Date): string {
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  const h = `${d.getHours()}`.padStart(2, '0');
  const m = `${d.getMinutes()}`.padStart(2, '0');
  return `${h}:${m}`;
}

export function combineDateTime(date: string, time: string): Date {
  const [y, mo, d] = date.split('-').map(Number);
  const [h, mi] = time.split(':').map(Number);
  const result = new Date();
  result.setFullYear(y, (mo ?? 1) - 1, d ?? 1);
  result.setHours(h ?? 0, mi ?? 0, 0, 0);
  return result;
}

export function addMinutesToTime(time: string, minutes: number): string {
  const [h, m] = time.split(':').map(Number);
  const total = (h ?? 0) * 60 + (m ?? 0) + minutes;
  const safe = ((total % (24 * 60)) + 24 * 60) % (24 * 60);
  const hh = `${Math.floor(safe / 60)}`.padStart(2, '0');
  const mm = `${safe % 60}`.padStart(2, '0');
  return `${hh}:${mm}`;
}

export function diffMinutes(startTime: string, endTime: string): number {
  const [sh, sm] = startTime.split(':').map(Number);
  const [eh, em] = endTime.split(':').map(Number);
  return ((eh ?? 0) * 60 + (em ?? 0)) - ((sh ?? 0) * 60 + (sm ?? 0));
}

export interface ConflictCheckInput {
  /** All same-week appointments on record (mock API response). */
  existing: AppointmentRecord[];
  /** Staff being assigned. */
  staffId: string;
  /** Proposed date (YYYY-MM-DD). */
  date: string;
  /** Proposed HH:mm range. */
  startTime: string;
  endTime: string;
  /** The appointment being edited, if any (excluded from self-conflict). */
  ignoreId?: string;
}

export function hasStaffConflict(input: ConflictCheckInput): boolean {
  const start = combineDateTime(input.date, input.startTime).getTime();
  const end = combineDateTime(input.date, input.endTime).getTime();
  if (end <= start) return false;

  return input.existing.some(apt => {
    if (apt.id === input.ignoreId) return false;
    if (apt.staffId !== input.staffId) return false;
    if (apt.status === 'cancelled' || apt.status === 'no_show') return false;
    const aStart = new Date(apt.startTime).getTime();
    const aEnd = new Date(apt.endTime).getTime();
    return start < aEnd && end > aStart;
  });
}
